import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EridLogoSvg from '@static/images/domruid-logo.svg';
import styled from 'styled-components';

import { useClickOutside } from '@r1-frontend/entities/Layout/_deps/helpers/hooks/useClickOutside';
import isSso from '@r1-frontend/entities/Layout/_deps/helpers/isSso';
import { Agreement } from '@r1-frontend/entities/Layout/Header/commonComponents/AgreementInfo';
import { breakpoints } from '@r1-frontend/entities/Layout/Header/types';
import { setIsAuth } from '@r1-frontend/entities/stores/auth/actionCreators';
import { getInfoAll } from '@r1-frontend/entities/stores/auth/actions/getInfoAll';
import { logout } from '@r1-frontend/entities/stores/auth/actions/logout';
import selectAgreements from '@r1-frontend/entities/stores/auth/selectors/selectAgreements';
import { ILinksData, selectClientPersonalLinksForHeader } from '@r1-frontend/entities/stores/auth/selectors/selectClientProducts';
import { TState } from '@r1-frontend/entities/stores/city/reducer';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { BlindMenu } from '@r1-frontend/ui-react/components/BlindMenu';
import {
    BlindMenuWithBtn,
    EElemType,
    TElemData,
} from '@r1-frontend/ui-react/components/BlindMenu/BlindMenuWithBtn';
import Button from '@r1-frontend/ui-react/components/buttons/button';
import { Logout } from '@r1-frontend/ui-react/components/svg/arrows';
import { DocumentText } from '@r1-frontend/ui-react/components/svg/content';
import { User, UserSquare } from '@r1-frontend/ui-react/components/svg/users';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { USER_CHANGE_ROUTE } from '@r1-frontend/shared/constants/routes';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import * as ST from './styled';

export const EridLogo = styled.img.attrs({
    src: EridLogoSvg,
})`
    height: 20px;
`;

const iconsMap = {
    'user-square': <UserSquare size={20}/>,
};

const getIcon = (link: ILinksData) => {
    const icon = link.icon && iconsMap[link.icon as keyof typeof iconsMap];
    if (icon) {
        return icon;
    }
};

const ProfileContainer = () => {
    const { links, linksForActionSearch, isNeedToRequest } = useSelector(selectClientPersonalLinksForHeader);
    const agreements = useSelector(selectAgreements);
    const dispatch = useDispatch();

    const ssoConfig = useSelector(
        (state: TState) => state.city.currentCity.sso,
    );

    useEffect(()=> {
        if (isNeedToRequest) {
            dispatch(getInfoAll(['personal', 'contacts', 'products']));
        }
    }, [dispatch, isNeedToRequest]);

    const sendEvent = useCallback((action) => {
        if (action) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.profileMenu,
                action,
            });
        }
    }, []);

    const onClickLinkHandler = useCallback((id: string) => {
        const item = linksForActionSearch[id];
        sendEvent(item.action);
    }, [linksForActionSearch, sendEvent]);

    const blindMenuRef = useRef<HTMLDivElement>(null);
    const agreementMenuRef = useRef<HTMLDivElement>(null);

    const [isDisplay, setIsDisplay] = useState(false);
    const [isDisplayAgreement, setIsDisplayAgreement] = useState(false);

    const isMobile = useMatchMediaByWidth(breakpoints.mobile, false, true);

    const onOutClick = useCallback((event) => {
        const path = event.composedPath();
        const isBlindMenu = path.includes(blindMenuRef?.current);
        const isAgreementMenu = path.includes(agreementMenuRef?.current);

        if (!isBlindMenu) {
            setIsDisplay(false);
        }

        if (!isAgreementMenu) {
            setIsDisplayAgreement(false);
        }
    }, []);

    useClickOutside(!isDisplay, onOutClick);
    useClickOutside(!isDisplayAgreement, onOutClick);

    const agreementDetailClickHandler = useCallback(() => {
        setIsDisplayAgreement(prevState => !prevState);
        if (!isDisplayAgreement) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.agreementMenu,
                action: 'open_agreement_details_menu',
            });
        }
    }, [isDisplayAgreement]);

    const profileMenuClickHandler = useCallback(() => {
        setIsDisplay(prevState => !prevState);
        if (!isDisplay) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.profileMenu,
                action: 'open_profile_menu',
            });
        }
    }, [isDisplay]);

    const handleManageAccount = () => {
        sendEvent('click_profile_erid');
    };

    const handleChangeAgreement = () => {
        sendEvent('click_change_agreement');
        window.location.replace(
            `${USER_CHANGE_ROUTE}?referrer=${window.location.pathname}`,
        );
    };

    const logoutHandler = useCallback(() => {
        sendEvent('click_exit');
        dispatch(setIsAuth(false));
        dispatch(logout());
    }, [dispatch, sendEvent]);

    const isDisplayChangeAgreement = useMemo(() => {
        return isSso() && agreements.length > 1;
    }, [agreements.length]);

    const menuElements: TElemData[] = useMemo(() => [
        ...(ssoConfig?.url
            ? [
                {
                    id: 'manage-account',
                    type: EElemType.button,
                    text: 'Управление аккаунтом',
                    link: ssoConfig.url,
                    target: '_blank',
                    onClick: handleManageAccount,
                    iconRight: <EridLogo />,
                },
                {
                    id: 'divider-1',
                    type: EElemType.divider,
                },
            ] as TElemData[]
            : []),
        ...links.map((link) => ({
            ...link,
            type: EElemType.button,
            iconRight: getIcon(link),
        })),
        ...(isDisplayChangeAgreement
            ? [
                {
                    id: 'change-agreement',
                    type: EElemType.button,
                    text: 'Сменить договор',
                    onClick: handleChangeAgreement,
                },
            ]
            : []),
        {
            id: 'divider-2',
            type: EElemType.divider,
        },
        {
            id: 'logout',
            type: EElemType.button,
            text: 'Выход',
            onClick: logoutHandler,
            iconRight: <Logout size={20}/>,
        },
    ], [isDisplayChangeAgreement, links, logoutHandler]);

    return (
        <ST.ProfileAuthWrapper>
            <BlindMenu
                activeComponent={<Button
                    rounded={true}
                    bType={'secondary'}
                    size={'medium'}
                    Icon={<DocumentText size={20}/>}
                />}
                blindMenuRef={agreementMenuRef}
                isMobileView={isMobile}
                displayDropDown={isDisplayAgreement}
                onClick={agreementDetailClickHandler}
            >
                <Agreement/>
            </BlindMenu>
            <BlindMenuWithBtn
                Button={<Button
                    rounded={true}
                    bType={'secondary'}
                    size={'medium'}
                    Icon={<User size={20}/>}
                />}
                blindMenuRef={blindMenuRef}
                isMobileView={isMobile}
                elements={menuElements}
                displayDropDown={isDisplay}
                onClick={profileMenuClickHandler}
                onElementClick={onClickLinkHandler}
                data-test='auth-menu'
            />
        </ST.ProfileAuthWrapper>
    );
};

export default ProfileContainer;
